import { Permisos } from '@/shared/constants/permisos';
import type { TourDefinicion } from '@/features/guia-interactiva/types/guia-interactiva.types';

/**
 * Tour específico de la barra de acciones del editor: un paso por cada
 * ícono/botón, filtrado por permisos del usuario. Complementa al tour
 * general `recet-tecnologia-editor`.
 *
 * Muchos botones son condicionales por estado de la tecnología (ej. "Publicar"
 * solo aparece en `pendiente_aprobacion` para el aprobador). El filtro runtime
 * de `useDriver` descarta pasos cuyo selector no está en el DOM en el momento,
 * así que cada usuario verá naturalmente el subconjunto aplicable a su rol y
 * al estado actual de la tecnología abierta.
 *
 * No auto-dispara: se lanza únicamente desde el botón ? (dropdown) del editor.
 */
export const tourRecetTecnologiaEditorAcciones: TourDefinicion = {
  codigo: 'recet-tecnologia-editor-acciones',
  titulo: 'Acciones del editor',
  descripcion:
    'Recorrido por cada botón e ícono de la barra de acciones del editor: workflow, exportar, paneles laterales.',
  permisoMenu: Permisos.TECNOLOGIAS_VER,
  soloContextual: true,
  pasos: [
    // ── Botones de workflow (condicionales por estado + permiso) ──
    {
      id: 'recet-tecnologia-editor-acciones:solicitar-aprobacion',
      selector: '[data-testid="recet-tecnologia-editor.btn-solicitar-aprobacion"]',
      titulo: 'Solicitar aprobación',
      descripcion: 'Envía la tecnología al aprobador para revisión. Disponible en estado borrador.',
      permisoRequerido: Permisos.TECNOLOGIAS_SOLICITAR_APROBACION,
      side: 'bottom',
      align: 'end',
    },
    {
      id: 'recet-tecnologia-editor-acciones:retirar',
      selector: '[data-testid="recet-tecnologia-editor.btn-retirar"]',
      titulo: 'Retirar solicitud',
      descripcion:
        'Retira una solicitud de aprobación en curso y devuelve la tecnología a borrador para seguir editando.',
      permisoRequerido: Permisos.TECNOLOGIAS_RETIRAR_SOLICITUD,
      side: 'bottom',
      align: 'end',
    },
    {
      id: 'recet-tecnologia-editor-acciones:publicar',
      selector: '[data-testid="recet-tecnologia-editor.btn-publicar"]',
      titulo: 'Publicar',
      descripcion:
        'Aprueba y publica una nueva versión. Antes de publicar, se valida que no existan cambios ni comentarios pendientes en ninguna sección.',
      permisoRequerido: Permisos.TECNOLOGIAS_APROBAR,
      side: 'bottom',
      align: 'end',
    },
    {
      id: 'recet-tecnologia-editor-acciones:rechazar',
      selector: '[data-testid="recet-tecnologia-editor.btn-rechazar"]',
      titulo: 'Rechazar',
      descripcion:
        'Rechaza la solicitud de aprobación. Se pedirá un comentario con el motivo que el editor recibirá.',
      permisoRequerido: Permisos.TECNOLOGIAS_APROBAR,
      side: 'bottom',
      align: 'end',
    },
    {
      id: 'recet-tecnologia-editor-acciones:solicitar-reapertura',
      selector: '[data-testid="recet-tecnologia-editor.btn-solicitar-reapertura"]',
      titulo: 'Solicitar reapertura',
      descripcion:
        'Sobre una tecnología ya publicada, solicita al aprobador reabrirla para editar.',
      permisoRequerido: Permisos.TECNOLOGIAS_SOLICITAR_REAPERTURA,
      side: 'bottom',
      align: 'end',
    },
    {
      id: 'recet-tecnologia-editor-acciones:aprobar-reapertura',
      selector: '[data-testid="recet-tecnologia-editor.btn-aprobar-reapertura"]',
      titulo: 'Aprobar reapertura',
      descripcion: 'Autoriza la reapertura solicitada. La tecnología vuelve a estado borrador.',
      permisoRequerido: Permisos.TECNOLOGIAS_APROBAR_REAPERTURA,
      side: 'bottom',
      align: 'end',
    },
    {
      id: 'recet-tecnologia-editor-acciones:rechazar-reapertura',
      selector: '[data-testid="recet-tecnologia-editor.btn-rechazar-reapertura"]',
      titulo: 'Rechazar reapertura',
      descripcion:
        'Rechaza la solicitud de reapertura con un motivo. La tecnología sigue publicada.',
      permisoRequerido: Permisos.TECNOLOGIAS_APROBAR_REAPERTURA,
      side: 'bottom',
      align: 'end',
    },

    // ── Íconos de acciones directas (sin permiso o con permiso puntual) ──
    {
      id: 'recet-tecnologia-editor-acciones:recargar',
      selector: '[data-testid="recet-tecnologia-editor.btn-recargar"]',
      titulo: 'Recargar',
      descripcion:
        'Vuelve a cargar los datos del servidor por si otro usuario cambió algo mientras estabas en la pantalla.',
      side: 'bottom',
      align: 'end',
    },
    {
      id: 'recet-tecnologia-editor-acciones:descargar',
      selector: '[data-testid="recet-tecnologia-editor.btn-descargar"]',
      titulo: 'Descargar original',
      descripcion: 'Descarga el archivo Word original desde el cual se importó esta tecnología.',
      permisoRequerido: Permisos.TECNOLOGIAS_DESCARGAR,
      side: 'bottom',
      align: 'end',
    },
    {
      id: 'recet-tecnologia-editor-acciones:exportar',
      selector: '[data-testid="recet-tecnologia-editor.btn-exportar"]',
      titulo: 'Exportar',
      descripcion:
        'Exporta el contenido actual a Word, PDF o HTML para compartirlo fuera del portal.',
      permisoRequerido: Permisos.TECNOLOGIAS_EXPORTAR,
      side: 'bottom',
      align: 'end',
    },
    {
      id: 'recet-tecnologia-editor-acciones:eliminar',
      selector: '[data-testid="recet-tecnologia-editor.btn-eliminar"]',
      titulo: 'Eliminar tecnología',
      descripcion:
        'Elimina la tecnología completa. Pide confirmación y la operación queda en auditoría.',
      permisoRequerido: Permisos.TECNOLOGIAS_ELIMINAR,
      side: 'bottom',
      align: 'end',
    },

    // ── Paneles laterales (tabs del lateral derecho) ──
    {
      id: 'recet-tecnologia-editor-acciones:tab-cambios',
      selector: '[data-testid="recet-tecnologia-editor.tab-cambios"]',
      titulo: 'Control de cambios',
      descripcion:
        'Abre el panel de sugerencias: revisa, acepta o rechaza cambios propuestos por el editor.',
      permisoRequerido: Permisos.SUGERENCIAS_VER,
      side: 'bottom',
      align: 'end',
    },
    {
      id: 'recet-tecnologia-editor-acciones:tab-comentarios',
      selector: '[data-testid="recet-tecnologia-editor.tab-comentarios"]',
      titulo: 'Comentarios',
      descripcion:
        'Abre el panel de comentarios de la sección activa. Los comentarios son la conversación entre editor y aprobador.',
      permisoRequerido: Permisos.COMENTARIOS_VER,
      side: 'bottom',
      align: 'end',
    },
    {
      id: 'recet-tecnologia-editor-acciones:tab-referencias',
      selector: '[data-testid="recet-tecnologia-editor.tab-referencias"]',
      titulo: 'Referencias bibliográficas',
      descripcion: 'Abre el panel de referencias bibliográficas asociadas a la tecnología.',
      permisoRequerido: Permisos.REFERENCIAS_VER,
      side: 'bottom',
      align: 'end',
    },
    {
      id: 'recet-tecnologia-editor-acciones:tab-versiones',
      selector: '[data-testid="recet-tecnologia-editor.tab-versiones"]',
      titulo: 'Historial de versiones',
      descripcion:
        'Abre el historial de versiones publicadas de la tecnología para revisar cambios entre versiones.',
      permisoRequerido: Permisos.VERSIONES_VER,
      side: 'bottom',
      align: 'end',
    },
  ],
};
